import { createFileRoute, Link } from '@tanstack/react-router'
import { PageContentWrapper } from '@unwrittenmedia/simple-components'
import { useAccount } from '@/contexts/AccountContext'
import { useCompleteAuthRedirect } from '@/hooks/useFirebaseAuth'

/**
 * Google Redirect Landing Page
 *
 * The page Google's sign-in redirect round-trip lands on: it either kicks off
 * `signInWithRedirect` (fresh visit from the `/login` page's Google button) or completes it
 * (returning from Google) via the shared `useCompleteAuthRedirect` hook.
 *
 * Deliberately has no `beforeLoad` auth guard, same as `/login`, so it's reachable while signed out.
 */
export const Route = createFileRoute('/login-gredir')({
  validateSearch: (search: Record<string, unknown>) => ({
    app: typeof search.app === 'string' && search.app.length > 0 ? search.app : undefined,
    to: typeof search.to === 'string' && search.to.length > 0 ? search.to : undefined,
  }),
  component: LoginGredirPage,
})

function LoginGredirPage() {
  const { dispatch, state: account } = useAccount()
  const { app: appId, to } = Route.useSearch()

  const { error } = useCompleteAuthRedirect({
    app: appId,
    to,
    isAuthenticated: account.isAuthenticated,
    dispatch,
    autoStartGoogleRedirect: true,
  })

  return (
    <PageContentWrapper className="bg-[radial-gradient(ellipse_at_top,_#bfdbfe_0%,_transparent_80%)]">
      <div className="flex flex-col justify-center min-h-screen">
        <div className="w-full max-w-sm mx-auto bg-white dark:bg-mist-900 rounded-2xl shadow-lg border border-gray-100 dark:border-mist-800 px-8 py-10">
          <h1 className="text-2xl font-bold mb-1 text-gray-900 dark:text-white tracking-tight">
            Logging in, one moment
          </h1>

          {error && (
            <div className="mt-5 p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-600 text-sm font-medium">{error}</p>
            </div>
          )}

          <div className="flex justify-center my-8">
            <svg
              className="animate-spin h-8 w-8 text-gray-400 dark:text-mist-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>

          <Link
            to="/login"
            className="w-full flex items-center justify-center text-xs text-gray-400 dark:text-mist-600 hover:text-gray-600 dark:hover:text-mist-400 transition"
          >
            Stuck? Try logging in again
          </Link>
        </div>
      </div>
    </PageContentWrapper>
  )
}
